import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import {
    LayoutDashboard,
    Package,
    Heart,
    User,
    LogOut,
    ChevronRight,
    Clock,
    ShoppingBag,
    CheckCircle,
    Info,
    Settings
} from 'lucide-react';
import './MonCompte.css';

const MonCompte = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(pb.authStore.model);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const { addToCart } = useCart();
    const { favorites, toggleFavorite, isFavorite } = useFavorites();

    // Preferences state: saved in localStorage for this demo
    const [preferences, setPreferences] = useState(() => {
        const saved = localStorage.getItem('gourmet_preferences');
        return saved ? JSON.parse(saved) : {
            chocolate: false,
            fruity: false,
            vanilla: false,
            nutty: false,
            glutenFree: false,
            vegan: false
        };
    });

    useEffect(() => {
        window.scrollTo(0, 0);

        // Update user state on auth change
        const unsubscribe = pb.authStore.onChange((token, model) => {
            setUser(model);
        });

        if (user) {
            fetchOrders();
        }

        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (user) {
            fetchOrders();
        }
    }, [user]);

    // Smooth scroll to top on tab change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab]);

    useEffect(() => {
        localStorage.setItem('gourmet_preferences', JSON.stringify(preferences));
    }, [preferences]);

    const fetchOrders = async () => {
        if (!user) return;
        setLoadingOrders(true);
        try {
            // Find orders for this user by email or ID
            const records = await pb.collection('commandes').getList(1, 20, {
                filter: `client_email = "${user.email}"`,
                sort: '-created',
            });
            setOrders(records.items);
        } catch (err) {
            console.error("Error fetching user orders:", err);
        } finally {
            setLoadingOrders(false);
        }
    };

    const handleLogout = () => {
        pb.authStore.clear();
        window.location.href = '/';
    };

    const togglePref = (key) => {
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleReorder = (order) => {
        const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
        if (items && Array.isArray(items)) {
            items.forEach(item => {
                addToCart({
                    id: item.id,
                    name: item.name,
                    prix: item.price,
                    image: null
                });
            });
            alert("Articles ajoutés au panier !");
        }
    };

    if (!user) {
        return (
            <div className="mon-compte-page">
                <div className="container" style={{ textAlign: 'center', padding: '100px 20px' }}>
                    <div className="account-card" style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <User size={48} color="var(--color-gold)" style={{ marginBottom: '20px' }} />
                        <h2>Espace Client</h2>
                        <p style={{ margin: '20px 0', color: '#666' }}>Connectez-vous pour suivre vos commandes et retrouver vos favoris.</p>
                        <button className="btn" style={{ width: '100%', marginBottom: '12px' }} onClick={() => document.querySelector('.user-icon-btn').click()}>
                            Se connecter
                        </button>
                        <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => navigate('/')}>
                            Retour au site
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <div className="tab-pane">
                        <div className="dashboard-grid">
                            <div className="account-card">
                                <div className="card-icon"><Package size={24} /></div>
                                <h3>Dernière Commande</h3>
                                {orders.length > 0 ? (
                                    <div>
                                        <p style={{ fontWeight: 'bold' }}>#{orders[0].id.substring(0, 8)}</p>
                                        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
                                            Le {new Date(orders[0].created).toLocaleDateString()}
                                        </p>
                                        <span className={`status-badge status-${orders[0].status}`}>
                                            {orders[0].status === 'pending' ? 'En attente' :
                                                orders[0].status === 'confirmed' ? 'En préparation' :
                                                    orders[0].status === 'completed' ? 'Prête / Récupérée' : 'Annulée'}
                                        </span>
                                    </div>
                                ) : (
                                    <p style={{ color: '#999' }}>Aucune commande récente.</p>
                                )}
                                <button
                                    className="btn btn-small"
                                    style={{ marginTop: '20px', width: '100%' }}
                                    onClick={() => setActiveTab('orders')}
                                >
                                    Historique complet
                                </button>
                            </div>

                            <div className="account-card">
                                <div className="card-icon"><Heart size={24} /></div>
                                <h3>Mes Favoris</h3>
                                <p style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{favorites.length} créations</p>
                                <p style={{ color: '#666', marginBottom: '15px' }}>Retrouvez vos coups de cœur à tout moment.</p>
                                <button
                                    className="btn btn-small"
                                    style={{ marginTop: '20px', width: '100%' }}
                                    onClick={() => setActiveTab('favorites')}
                                >
                                    Voir mes favoris
                                </button>
                            </div>

                            <div className="account-card">
                                <div className="card-icon"><User size={24} /></div>
                                <h3>Mon Profil</h3>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                    {Object.entries(preferences).filter(([_, v]) => v).length > 0 ? (
                                        Object.entries(preferences).filter(([_, v]) => v).map(([k]) => (
                                            <span key={k} style={{ fontSize: '0.75rem', background: '#f0f0f0', padding: '3px 8px', borderRadius: '10px' }}>
                                                {k === 'chocolate' ? 'Chocolat' :
                                                    k === 'fruity' ? 'Fruits' :
                                                        k === 'vanilla' ? 'Vanille' :
                                                            k === 'nutty' ? 'Fruits à coque' :
                                                                k === 'glutenFree' ? 'Sans Gluten' : 'Vegan'}
                                            </span>
                                        ))
                                    ) : (
                                        <p style={{ color: '#999', fontSize: '0.9rem' }}>Définissez vos préférences pour un accueil personnalisé.</p>
                                    )}
                                </div>
                                <button
                                    className="btn btn-small"
                                    style={{ marginTop: '20px', width: '100%' }}
                                    onClick={() => setActiveTab('profil')}
                                >
                                    Modifier mon profil
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 'orders':
                return (
                    <div className="tab-pane">
                        {/* Desktop Table View */}
                        <div className="orders-table-container">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>Commande</th>
                                        <th>Date</th>
                                        <th>Articles</th>
                                        <th>Total</th>
                                        <th>Statut</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingOrders ? (
                                        <tr><td colSpan="6" style={{ textAlign: 'center' }}>Chargement...</td></tr>
                                    ) : orders.length === 0 ? (
                                        <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>Vous n'avez pas encore passé de commande.</td></tr>
                                    ) : (
                                        orders.map(order => (
                                            <tr key={order.id}>
                                                <td><span style={{ fontSize: '0.8rem', opacity: 0.6 }}>#{order.id.substring(0, 8)}</span></td>
                                                <td>{new Date(order.created).toLocaleDateString()}</td>
                                                <td>
                                                    {(() => {
                                                        const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
                                                        return items ? items.map(it => it.name).join(', ') : '-';
                                                    })()}
                                                </td>
                                                <td><strong>{order.total} €</strong></td>
                                                <td>
                                                    <span className={`status-badge status-${order.status}`}>
                                                        {order.status === 'pending' ? 'En attente' :
                                                            order.status === 'confirmed' ? 'Prêt' :
                                                                order.status === 'completed' ? 'Terminé' : 'Annulé'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => handleReorder(order)}
                                                        className="btn btn-small"
                                                        style={{ padding: '5px 10px', fontSize: '0.7rem' }}
                                                    >
                                                        Recommander
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Ticket View */}
                        <div className="mobile-order-tickets">
                            {orders.length === 0 ? (
                                <p style={{ textAlign: 'center', padding: '20px' }}>Aucune commande.</p>
                            ) : (
                                orders.map(order => (
                                    <div key={order.id} className="order-ticket">
                                        <div className="order-ticket-header">
                                            <div>
                                                <div className="ticket-id">#{order.id.substring(0, 8)}</div>
                                                <div className="ticket-date">{new Date(order.created).toLocaleDateString()}</div>
                                            </div>
                                            <span className={`status-badge status-${order.status}`}>
                                                {order.status === 'pending' ? 'En attente' : order.status === 'confirmed' ? 'Prêt' : 'Terminé'}
                                            </span>
                                        </div>
                                        <div className="order-ticket-body">
                                            <p><strong>Articles:</strong> {(() => {
                                                const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
                                                return items ? items.map(it => it.name).join(', ') : '-';
                                            })()}</p>
                                        </div>
                                        <div className="order-ticket-footer">
                                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{order.total} €</div>
                                            <button
                                                onClick={() => handleReorder(order)}
                                                className="btn btn-small"
                                            >
                                                Recommander
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );

            case 'favorites':
                return (
                    <div className="tab-pane">
                        {favorites.length === 0 ? (
                            <div className="account-card" style={{ textAlign: 'center', padding: '60px' }}>
                                <Heart size={40} color="#ddd" style={{ marginBottom: '15px' }} />
                                <p>Vous n'avez aucune création en favoris pour le moment.</p>
                                <a href="/gateaux" className="btn" style={{ marginTop: '20px' }}>Découvrir nos gâteaux</a>
                            </div>
                        ) : (
                            <div className="fav-grid">
                                {favorites.map(fav => (
                                    <div key={fav.id} className="fav-card">
                                        {fav.image && <img src={fav.image} alt={fav.name} className="fav-img" />}
                                        <div className="fav-info">
                                            <h4 style={{ color: 'var(--color-brown)', marginBottom: '5px' }}>{fav.name}</h4>
                                            <p style={{ fontSize: '0.9rem', color: 'var(--color-gold)', marginBottom: '15px' }}>{fav.prix} €</p>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <button
                                                    className="btn btn-small"
                                                    style={{ flex: 1 }}
                                                    onClick={() => addToCart(fav)}
                                                >
                                                    <ShoppingBag size={14} style={{ marginRight: '5px' }} />
                                                    Ajouter
                                                </button>
                                                <button
                                                    onClick={() => toggleFavorite(fav)}
                                                    style={{ background: 'none', border: '1px solid #eee', borderRadius: '4px', padding: '5px 10px' }}
                                                >
                                                    <Heart size={16} fill="red" color="red" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 'profil':
                return (
                    <div className="tab-pane">
                        <div className="account-card">
                            <h3 style={{ marginBottom: '10px' }}>Mon Profil & Préférences</h3>
                            <p style={{ color: '#666', marginBottom: '30px' }}>Gérez vos informations et vos goûts pour un accueil personnalisé chez Millet.</p>

                            <div style={{ marginBottom: '40px', paddingBottom: '30px', borderBottom: '1px solid #eee' }}>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                    <div style={{ width: '80px', height: '80px', background: 'var(--color-gold-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-brown)', fontSize: '2rem', fontWeight: 'bold' }}>
                                        {user.email.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '1.2rem' }}>{user.name || "Client Gourmet"}</h4>
                                        <p style={{ margin: 0, color: '#666' }}>{user.email}</p>
                                        {user.phone && <p style={{ margin: '5px 0 0 0', color: 'var(--color-gold)', fontSize: '0.9rem', fontWeight: '500' }}>📞 {user.phone}</p>}
                                    </div>
                                </div>
                            </div>

                            <h4 style={{ marginBottom: '20px', color: 'var(--color-brown)' }}>Mes Goûts Favoris</h4>
                            <div className="prefs-grid">
                                <div className={`pref-item ${preferences.chocolate ? 'active' : ''}`} onClick={() => togglePref('chocolate')}>
                                    <div className="pref-icon">🍫</div>
                                    <span>Fan de Chocolat</span>
                                </div>
                                <div className={`pref-item ${preferences.fruity ? 'active' : ''}`} onClick={() => togglePref('fruity')}>
                                    <div className="pref-icon">🍓</div>
                                    <span>Passion Fruits</span>
                                </div>
                                <div className={`pref-item ${preferences.vanilla ? 'active' : ''}`} onClick={() => togglePref('vanilla')}>
                                    <div className="pref-icon">🍦</div>
                                    <span>Douceur Vanille</span>
                                </div>
                                <div className={`pref-item ${preferences.nutty ? 'active' : ''}`} onClick={() => togglePref('nutty')}>
                                    <div className="pref-icon">🥜</div>
                                    <span>Fruits à coque</span>
                                </div>
                                <div className={`pref-item ${preferences.glutenFree ? 'active' : ''}`} onClick={() => togglePref('glutenFree')}>
                                    <div className="pref-icon">🌾</div>
                                    <span>Sans Gluten</span>
                                </div>
                                <div className={`pref-item ${preferences.vegan ? 'active' : ''}`} onClick={() => togglePref('vegan')}>
                                    <div className="pref-icon">🌿</div>
                                    <span>Végan / Végétal</span>
                                </div>
                            </div>

                            <div style={{ marginTop: '40px', padding: '20px', background: 'var(--color-cream)', borderRadius: '8px', border: '1px solid var(--color-gold-light)', display: 'flex', gap: '15px' }}>
                                <Info size={24} color="var(--color-gold)" />
                                <p style={{ fontSize: '0.9rem', color: 'var(--color-brown)' }}>
                                    Ces préférences seront utilisées pour personnaliser votre expérience sur notre site et vous envoyer des recommandations ciblées.
                                </p>
                            </div>

                            <div className="mobile-only-logout" style={{ marginTop: '30px' }}>
                                <button
                                    onClick={handleLogout}
                                    className="btn"
                                    style={{ width: '100%', background: '#fff', color: '#e74c3c', border: '1px solid #e74c3c' }}
                                >
                                    <LogOut size={18} style={{ marginRight: '10px' }} />
                                    Se déconnecter
                                </button>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="mon-compte-page">
            <div className="account-container">
                <header className="account-header">
                    <span className="account-subtitle">Bienvenue chez Millet</span>
                    <h1>Bonjour, {user.name || user.email.split('@')[0]}</h1>
                    <div className="divider" style={{ margin: '20px auto' }}></div>
                </header>

                <nav className="account-horizontal-tabs">
                    <button
                        className={`horizontal-tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        <LayoutDashboard size={18} /> Tableau de Bord
                    </button>
                    <button
                        className={`horizontal-tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('orders')}
                    >
                        <Package size={18} /> Mes Commandes
                    </button>
                    <button
                        className={`horizontal-tab-btn ${activeTab === 'favorites' ? 'active' : ''}`}
                        onClick={() => setActiveTab('favorites')}
                    >
                        <Heart size={18} /> Mes Favoris
                    </button>
                    <button
                        className={`horizontal-tab-btn ${activeTab === 'profil' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profil')}
                    >
                        <User size={18} /> Mon Profil
                    </button>
                </nav>

                <div className="account-layout">
                    <aside className="account-sidebar">
                        <nav className="account-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                                onClick={() => setActiveTab('dashboard')}
                            >
                                <LayoutDashboard size={18} /> <span>Tableau de Bord</span>
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                                onClick={() => setActiveTab('orders')}
                            >
                                <Package size={18} /> <span>Mes Commandes</span>
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'favorites' ? 'active' : ''}`}
                                onClick={() => setActiveTab('favorites')}
                            >
                                <Heart size={18} /> <span>Mes Favoris</span>
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'profil' ? 'active' : ''}`}
                                onClick={() => setActiveTab('profil')}
                            >
                                <User size={18} /> <span>Mon Profil</span>
                            </button>

                            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                                <button
                                    className="tab-btn logout-btn-sidebar"
                                    onClick={handleLogout}
                                    style={{ color: '#e74c3c' }}
                                >
                                    <LogOut size={18} /> <span>Déconnexion</span>
                                </button>
                            </div>
                        </nav>
                    </aside>

                    <main className="account-content">
                        {renderTabContent()}
                    </main>
                </div>
            </div>

            {/* Mobile Bottom Navigation */}
            <nav className="mobile-bottom-nav">
                <button
                    className={`mobile-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    <LayoutDashboard size={24} />
                    <span>Accueil</span>
                </button>
                <button
                    className={`mobile-nav-item ${activeTab === 'orders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('orders')}
                >
                    <Package size={24} />
                    <span>Commandes</span>
                </button>
                <button
                    className={`mobile-nav-item ${activeTab === 'favorites' ? 'active' : ''}`}
                    onClick={() => setActiveTab('favorites')}
                >
                    <Heart size={24} />
                    <span>Favoris</span>
                </button>
                <button
                    className={`mobile-nav-item ${activeTab === 'profil' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profil')}
                >
                    <User size={24} />
                    <span>Profil</span>
                </button>
            </nav>
        </div>
    );
};

export default MonCompte;
